import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Motion, spring } from 'react-motion';
import styled from 'styled-components';

import HeightReporter from './ReactHeight';
import ChildObserver from './ChildObserver';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  max-height: 100%;
`;

// Based on https://github.com/nkbt/react-collapse

/** @visibleName Slide Toggle */
export class SlideToggle extends PureComponent {
  static propTypes = {
    open: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    springConfig: PropTypes.objectOf(PropTypes.number),
    onRest: PropTypes.func,
    // Renders an invisible layer behind the content which when clicked
    // on fires `onOverlayClick
    withOverlay: PropTypes.bool,
    onOverlayClick: PropTypes.func,
    dirty: PropTypes.bool,
    className: PropTypes.string,
  };

  static defaultProps = {
    withOverlay: false,
    onOverlayClick: Function.prototype,
  };

  constructor(props) {
    super(props);

    this.state = {
      dirty: true,
      height: -1,
      openChanged: false,
    };

    this.onHeightReady = this.onHeightReady.bind(this);
    this.onMutation = this.onMutation.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.height = 0;
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  UNSAFE_componentWillReceiveProps({ children, open }) {
    var dirty = this.props.dirty;

    if (children !== this.props.children) {
      dirty = true;
    }

    this.setState({
      dirty,
      openChanged: open !== this.props.open,
    });
  }

  onHeightReady(height) {
    this.setState({
      height,
      dirty: false,
    });
  }

  /* istanbul ignore next */
  onMutation() {
    // this can get called after the component was unmounted, causing various problems
    if (!this.mounted) {
      return;
    }

    this.setState({
      dirty: true,
    });

    this.forceUpdate();
  }

  getMotionHeight(height) {
    const { open, springConfig } = this.props;
    const { openChanged } = this.state;

    const newHeight = open ? Math.max(0, height) : 0;
    const skipAnimation = !openChanged || this.height === newHeight;

    const springHeight = spring(newHeight, {
      precision: 0.5,
      ...springConfig,
    });

    return skipAnimation ? newHeight : springHeight;
  }

  render() {
    const { height, dirty } = this.state;

    const content = (
      <HeightReporter dirty={dirty} onHeightReady={this.onHeightReady}>
        {this.props.children}
      </HeightReporter>
    );

    return (
      <Motion
        onRest={this.props.onRest}
        defaultStyle={{ height: Math.max(0, height) }}
        style={{ height: this.getMotionHeight(height) }}
      >
        {style => {
          this.height = style.height;

          var newStyle =
            this.props.open && this.height === height
              ? { height: 'auto' }
              : { height: style.height, overflow: 'hidden' };

          if (!this.height) {
            newStyle = { visibility: 'hidden', overflowY: 'auto' };
          }

          return (
            <div className={this.props.className}>
              {this.props.withOverlay && this.height ? (
                <Overlay onClick={this.props.onOverlayClick} />
              ) : null}

              <Content style={{ ...newStyle, ...style }}>
                <ChildObserver onMutation={this.onMutation}>
                  {content}
                </ChildObserver>
              </Content>
            </div>
          );
        }}
      </Motion>
    );
  }
}

export default SlideToggle;
